<template>
    <div class="registration-list">
        <heading :title="title" tag="registration-list-title">
            <b-dropdown v-if="$user.isSuperAdmin() || $user.isAdmin()"
                        text="Process Selected Registrations" variant="primary"
                        :disabled="!selectedEntries || !selectedEntries.length">
                <b-dropdown-item @click="numberEntries(false)">Assign Decal #</b-dropdown-item>
                <b-dropdown-item @click="markEntriesResolved(true)">
                    Mark as Resolved
                </b-dropdown-item>
                <b-dropdown-item @click="markEntriesResolved(false)">
                    Mark as Unresolved
                </b-dropdown-item>
                <b-dropdown-item @click="markEntriesMailed(true)">
                    Send Mailing Notification
                </b-dropdown-item>
                <b-dropdown-item @click="showAddNoteModal()">
                    Add Note
                </b-dropdown-item>
                <b-dropdown-divider/>
                <b-dropdown-item @click="numberEntries(true)">
                    Replace Decal #
                </b-dropdown-item>
            </b-dropdown>

            <export-button class="ml-3"
                           @export="exportList">
                Export
            </export-button>
        </heading>

        <data-table ref="dataTable"
                    persist-name="registration-filter"
                    :show-filters="true"
                    :show-pager="true"
                    :selectable-rows="true"
                    @row-selected="rowSelected"
                    :get-items="getEntries"
                    v-model="filters"
                    :fields="fields">

            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'id'">
                    {{ slotProps.row.item.id }}
                </div>
                <div v-if="slotProps.row.field.key === 'company'">
                    <router-link target="_blank"
                                 :to="`/company/view/${slotProps.row.item.equipment.company_id}`">
                        {{ slotProps.row.item.equipment.company.name }}
                    </router-link>
                </div>
                <div v-if="slotProps.row.field.key === 'vehicle_id'">
                    <router-link target="_blank"
                                 :to="`/equipment/view/${slotProps.row.item.equipment_id}`">
                        {{ slotProps.row.item.equipment.vehicle_id }}
                    </router-link>
                </div>
                <div v-if="slotProps.row.field.key === 'on_road'">
                    {{ slotProps.row.item.equipment.on_road ? 'On-Road' : 'Non-Road' }}
                </div>
                <div v-if="slotProps.row.field.key === 'engine_family'">
                    {{ slotProps.row.item.equipment.engine_family_name }}
                    <span v-if="slotProps.row.item.equipment.engine_tier">
                        (Tier {{ slotProps.row.item.equipment.engine_tier }})
                    </span>
                </div>
                <div v-if="slotProps.row.field.key === 'is_resolved'">
                    <template v-if="slotProps.row.item.is_resolved">
                        <b-icon-check-circle-fill variant="success"/>
                    </template>
                </div>
                <div v-if="slotProps.row.field.key === 'lastUserLog'">
                    <div v-if="slotProps.row.item.lastUserLog">
                        <b-badge size="sm" variant="" class="px-2 py-1 mr-2 mb-1">
                            <timestamp :date="slotProps.row.item.lastUserLog.created"/>
                        </b-badge>
                        <br/>
                        {{ slotProps.row.item.lastUserLog.text }}
                    </div>
                </div>
                <div v-if="slotProps.row.field.key === 'logs'">
                    <text-popover :id="`popover-${slotProps.row.item.id}`" class="mb-3"
                                  v-if="slotProps.row.item.userLogs.length > 1">
                        <div v-for="(entry,i) in slotProps.row.item.userLogs" :key="i">
                            <b-badge size="sm" variant="" class="px-2 py-1 mr-2 mb-1">
                                <timestamp :date="entry.created"/>
                            </b-badge>
                            <br/>
                            {{ entry.text }}
                        </div>
                    </text-popover>
                </div>
                <div v-if="slotProps.row.field.key === 'attestationStatus'">
                    <attestation-badge :equipment="slotProps.row.item.equipment"/>
                </div>
                <div v-if="slotProps.row.field.key === 'number'">
                    {{ slotProps.row.item.number }}
                </div>
                <div v-if="slotProps.row.field.key === 'tagDisplay'">
                    <strong>{{ slotProps.row.item.tagDisplay }}</strong>
                </div>
                <div v-if="slotProps.row.field.key === 'type'">
                    {{ slotProps.row.item.type }}
                </div>
            </template>

        </data-table>

        <b-modal id="add-note-modal" title="Add Note" @ok="addNote()">
            <b-form-input type="text" v-model="note"/>
        </b-modal>

    </div>
</template>

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import moment from 'moment'
import Api from '@/providers/Api'
import Export from '@/providers/Export'
import AttestationBadge from '@/components/AttestationBadge'
import Equipment from '@/models/Equipment'

export default Vue.extend({
    name: 'RegistrationList',
    components: {AttestationBadge},
    data() {
        return {
            types: [],
            fields: [
                {key: 'id', sortable: true, label: '#'},
                {key: 'number', sortable: true, label: 'Decal #'},
                {key: 'is_resolved', sortable: true, label: 'Resolved'},
                {key: 'lastUserLog', sortable: true, label: 'Last action'},
                {key: 'logs', sortable: false, label: 'History'},
                {key: 'tagDisplay', sortable: true, label: 'Tag'},
                {key: 'company', sortable: true, label: 'Company'},
                {key: 'vehicle_id', sortable: true, label: 'Equipment or Vehicle ID/Name'},
                {key: 'on_road', sortable: true, label: 'Equipment Category'},
                {key: 'engine_family', sortable: true, label: 'Engine Family / Tier'},
                {key: 'attestationStatus', sortable: true, label: 'Attestation Status'},
            ],
            filters: {
                number: {field: 'number', label: 'Decal #', type: 'string'},
                hasNumber: {field: 'hasNumber', label: 'Has Decal # Assigned', type: 'boolean'},
                is_resolved: {field: 'is_resolved', label: 'Resolved', type: 'boolean'},
                company_id: {
                    field: 'company_id',
                    type: 'multiselect',
                    search: (query) => {
                        return Api.getItems(this, 'company', {name: {field: 'name', value: query}})
                    },
                    apiTable: 'company',
                    labelField: 'name',
                    value: null,
                    label: 'Company'
                },
                on_road: this.getOnroadFilter(),
                vehicle_id: {field: 'vehicle_id', label: 'Equipment or Vehicle ID/Name', type: 'string'},
                efnStatus: {
                  field: 'efnStatus',
                  label: 'EFN status',
                  type: 'select',
                  options: ['EMPTY', 'NO MATCH', 'MATCH']
                },
            },
            title: 'Registrations',
            selectedEntries: [],
            selectedEntryIds: [],
            note: null, // for add note modal
        }
    },
    methods: {
        getEntries(listParams) {
            return this.$api.getItems(this, 'registration', this.filters, listParams, ['equipment'])
        },
        rowSelected(rows) {
            this.selectedEntries = rows
            this.selectedEntryIds = _.map(this.selectedEntries, (r) => {
                return r.id
            })
        },
        getOnroadFilter() {
            // HACK: hide on-road vehicle registrations until late 2023, as requirements for them only
            // start in 2024 (see AC #176).
            // After that date, show a selectable filter.
            const today = moment().toISOString() // e.g. 2022-04-18T17:38:13.932Z
            if (today < '2023-11-01') {
                return {field: 'on_road', type: 'hidden', value: '0'}
            } else {
                return {
                    field: 'on_road', label: 'Equipment Category', type: 'select',
                    options: [
                        {value: '1', text: 'On-Road'},
                        {value: '0', text: 'Non-Road'},
                    ]
                }
            }
        },
        showAddNoteModal() {
            this.$bvModal.show('add-note-modal')
        },
        async addNote() {
            await this.$api.addRegistrationNote(this, this.selectedEntryIds, this.note)
            this.$refs.dataTable.refresh()
            this.note = null
        },
        async markEntriesResolved(value) {
            await this.$api.updateRegistrationResolved(this, this.selectedEntryIds, value)
            this.$refs.dataTable.refresh()
        },
        async markEntriesMailed() {
            if (await this.$bvModal.msgBoxConfirm(`Send notifications that decals have been mailed for the selected registrations?`)) {
                await this.$api.sendRegistrationMailedNotification(this, this.selectedEntryIds)
                this.$refs.dataTable.refresh()
            }
        },
        async numberEntries(force) {
            let proceed = true
            if (force) {
                proceed = await this.$bvModal.msgBoxConfirm(`Assign a new Decal # to this registration?
                This should only be done if the previous decal has been reported as lost.`)
            }
            if (proceed) {
                await this.$api.numberRegistrations(this, this.selectedEntryIds, force)
                this.$refs.dataTable.refresh()
            }
        },
        getLastAction(reg) {
            if (reg.userLogs.length) {
                return reg.userLogs[0].created + ' ' + reg.userLogs[0].text
            }
            return null
        },
        getHistoryListing(reg) {
            if (reg.userLogs.length) {
                return _.reduce(reg.userLogs, (s, entry) => {
                        return s + entry.created + ' ' + entry.text + '\n'
                    },
                    '')
            }
            return null
        },

        async exportList(filetype) {
            this.$bvModal.msgBoxConfirm('Would you like up to five registrations to appear on the same row?', {
                okTitle: 'Yes',
                cancelTitle: 'No',
            })
                .then(async (confirm) => {
                    //  context
                    const contextTable = this.$refs.dataTable.getContext()
                    const context = contextTable ? {sortBy: contextTable.sortBy, perPage: 100000, currentPage: 1} : null
                    let items = await this.$api.getItems(this, 'registration', this.filters, context, ['equipment'])

                    // spit out up to 5 regs for the same company on each row
                    let rows = [[
                        'Company',
                        'Mailing Address',
                        'City/State/ZIP'
                    ]]
                    if (confirm) {
                        for (let i = 1;  i <= 5;  i++) {
                            const suffix = ' (' + i.toString() + ')'
                            rows[0] = _.concat(rows[0], [
                                'Equipment ID' + suffix,
                                'Decal #' + suffix,
                                'Tag' + suffix,
                                'On-Road' + suffix,
                                'Type' + suffix,
                                'Last action' + suffix,
                                'History' + suffix,
                                'Resolved' + suffix,
                            ])
                        }
                    } else {
                        rows[0] = _.concat(rows[0], [
                            'Equipment ID',
                            'Decal #',
                            'Tag',
                            'On-Road',
                            'Type',
                            'Last action',
                            'History',
                            'Resolved',
                        ])
                    }

                    // group rows by company
                    const itemsByCompany = _.groupBy(items, (reg) => {
                        return reg.equipment.company.id
                    })
                    // for each company, split regs into chunks of up to 5 and spit out a row for each chunk
                    _.each(itemsByCompany, (companyItems) => {
                        const companyChunks = _.chunk(companyItems, confirm ? 5 : 1)
                        _.each(companyChunks, (chunk) => { // chunk is up to 5 registrations
                            const company = chunk[0].equipment.company
                            const row = [
                                company.name,
                                company.mailing_address,
                                (company.city || '') + ' ' + (company.state || '') + ' ' + (company.zip || ''),
                            ]
                            _.each(chunk, (reg) => {
                                console.log('reg.equipment=', reg.equipment)
                                row.push(reg.equipment.vehicle_id)
                                row.push(reg.number)
                                row.push(reg.tagDisplay)
                                row.push(reg.equipment?.on_road ? 'Yes' : 'No')
                                row.push(Equipment.getTypeLabel(reg.equipment?.type))
                                row.push(this.getLastAction(reg))
                                row.push(this.getHistoryListing(reg))
                                row.push(reg.is_resolved)
                            })
                            rows.push(row)
                        })
                    })

                    Export.exportRows(rows, 'Equipment Registration', filetype)
                })
                .catch(() => {
                    this.$toasted.error('Unable to export data')
                })
        },
    }
})
</script>

<style lang="scss">
.registration-list {
    .page-search__filters {
        flex-wrap: wrap;
        flex-direction: row !important;

        .col {
            width: 50%;
            max-width: 100%;
            flex-basis: auto;
            @media(min-width: 768px) {
                width: 32%;
            }
            @media(min-width: 1200px) {
                width: auto;
                max-width: 500px;
            }
        }
    }
}
</style>
